import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { partnerService } from '@/services/partner';
import { PartnerEmail } from '@/types/partner';
import { getClientDisplayName } from '../utils/clientDisplay';

interface DeleteClientModalProps {
  isOpen: boolean;
  client: PartnerEmail | null;
  partnerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function DeleteClientModal({
  isOpen,
  client,
  partnerId,
  onClose,
  onSuccess,
}: DeleteClientModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleDelete = async () => {
    if (!client) return;
    setIsLoading(true);
    try {
      await partnerService.removeUser(partnerId, client.id);
      toast({
        title: 'Cliente excluído com sucesso.',
        description: `${getClientDisplayName(client)} não terá mais acesso.`,
      });
      onSuccess();
      onClose();
    } catch {
      toast({
        variant: 'error',
        title: 'Não foi possível excluir o cliente',
        description: 'Tente novamente ou entre em contato com o suporte.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle>Excluir cliente</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir {getClientDisplayName(client)}? Esta ação irá
            desabilitar o recebimento de reports por este cliente.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="dark"
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
